import type { AppLocale } from './locales'

const en = {
  title: 'Browser information', hint: 'Copy this information when reporting a problem to help pinpoint the cause.',
  copy: 'Copy information', copying: 'Copying…', copied: 'Copied', retryCopy: 'Retry copy',
  copyDone: 'Browser information copied. You can paste it into your feedback.',
  copyFailed: 'Could not copy. Try again, or expand Technical details and select the information manually.',
  environment: 'Environment', features: 'Key features', details: 'Technical details',
  browser: 'Browser', operatingSystem: 'Operating system', language: 'Browser language', timeZone: 'Time zone',
  screen: 'Screen size', viewport: 'Page viewport', pixelRatio: 'Pixel ratio', cssPixels: '{width} × {height} CSS px',
  unknown: 'Unknown', unknownVersion: 'Version unavailable', chromiumFork: 'Chromium-based browser',
  capture: 'Screen sharing', documentPip: 'Always-on-top widget', wasmSimd: 'Fast local recognition',
  supported: 'Supported', unavailable: 'Unavailable', restricted: 'Restricted', apiAvailable: 'API available', writable: 'Write test passed',
  featureHint: 'Detection does not grant permission. Screen sharing still requires your approval.',
  technicalHint: '“API available” means the interface is exposed; permissions, storage capacity and hardware acceleration are not verified.',
  userAgent: 'User-Agent', secureContext: 'Secure context', embedded: 'Embedded page', yes: 'Yes', no: 'No',
  offscreenCanvas: 'OffscreenCanvas', canvas2d: 'Canvas 2D', createImageBitmap: 'ImageBitmap', moduleWorker: 'Worker',
  subtleCrypto: 'Web Crypto', cacheApi: 'Cache API', indexedDb: 'IndexedDB', localStorage: 'localStorage',
  captureReason: 'Screen sharing restriction',
  captureIssues: {
    mobile: 'Mobile browser', 'insecure-context': 'Requires a secure context', embedded: 'Page is embedded',
    unsupported: 'Screen sharing API unavailable', 'policy-blocked': 'Blocked by page permissions policy',
  },
}

export const browserInfoMessages: Record<AppLocale, typeof en> = {
  en,
  'zh-CN': {
    title: '浏览器信息', hint: '反馈问题时，复制这些信息可以帮助更快定位原因。',
    copy: '复制浏览器信息', copying: '复制中…', copied: '已复制', retryCopy: '重试复制',
    copyDone: '浏览器信息已复制，可粘贴到问题反馈中。',
    copyFailed: '复制失败，请重试，或展开技术详情后手动选择信息复制。',
    environment: '环境信息', features: '关键功能', details: '技术详情',
    browser: '浏览器', operatingSystem: '操作系统', language: '浏览器语言', timeZone: '时区',
    screen: '屏幕尺寸', viewport: '页面视口', pixelRatio: '设备像素比', cssPixels: '{width} × {height} CSS 像素',
    unknown: '未知', unknownVersion: '版本未知', chromiumFork: 'Chromium 衍生浏览器',
    capture: '屏幕共享', documentPip: '置顶小组件', wasmSimd: '本地高速识别',
    supported: '支持', unavailable: '不可用', restricted: '当前受限', apiAvailable: '接口可用', writable: '写入正常',
    featureHint: '检测结果不代表已获得权限，屏幕共享仍需你手动授权。',
    technicalHint: '“接口可用”仅表示浏览器提供该接口，未验证权限、存储容量或硬件加速状态。',
    userAgent: 'User-Agent', secureContext: '安全上下文', embedded: '内嵌页面', yes: '是', no: '否',
    offscreenCanvas: 'OffscreenCanvas', canvas2d: 'Canvas 2D', createImageBitmap: 'ImageBitmap', moduleWorker: 'Worker',
    subtleCrypto: 'Web Crypto', cacheApi: 'Cache API', indexedDb: 'IndexedDB', localStorage: 'localStorage',
    captureReason: '屏幕共享受限原因',
    captureIssues: {
      mobile: '移动端浏览器', 'insecure-context': '需要安全上下文', embedded: '当前页面处于内嵌框架中',
      unsupported: '未检测到屏幕共享接口', 'policy-blocked': '页面权限策略禁止屏幕共享',
    },
  },
  'zh-TW': {
    title: '瀏覽器資訊', hint: '回報問題時，複製這些資訊可以協助更快找出原因。',
    copy: '複製瀏覽器資訊', copying: '複製中…', copied: '已複製', retryCopy: '重試複製',
    copyDone: '已複製瀏覽器資訊，可貼到問題回報中。',
    copyFailed: '複製失敗，請重試，或展開技術詳細資料後手動選取資訊複製。',
    environment: '環境資訊', features: '主要功能', details: '技術詳細資料',
    browser: '瀏覽器', operatingSystem: '作業系統', language: '瀏覽器語言', timeZone: '時區',
    screen: '螢幕尺寸', viewport: '頁面檢視區', pixelRatio: '裝置像素比', cssPixels: '{width} × {height} CSS 像素',
    unknown: '未知', unknownVersion: '版本未知', chromiumFork: 'Chromium 衍生瀏覽器',
    capture: '螢幕分享', documentPip: '置頂小工具', wasmSimd: '本機高速辨識',
    supported: '支援', unavailable: '無法使用', restricted: '目前受限', apiAvailable: '介面可用', writable: '寫入正常',
    featureHint: '偵測結果不代表已取得權限，螢幕分享仍需你手動授權。',
    technicalHint: '「介面可用」僅表示瀏覽器提供該介面，未驗證權限、儲存容量或硬體加速狀態。',
    userAgent: 'User-Agent', secureContext: '安全環境', embedded: '內嵌頁面', yes: '是', no: '否',
    offscreenCanvas: 'OffscreenCanvas', canvas2d: 'Canvas 2D', createImageBitmap: 'ImageBitmap', moduleWorker: 'Worker',
    subtleCrypto: 'Web Crypto', cacheApi: 'Cache API', indexedDb: 'IndexedDB', localStorage: 'localStorage',
    captureReason: '螢幕分享受限原因',
    captureIssues: {
      mobile: '行動裝置瀏覽器', 'insecure-context': '需要安全環境', embedded: '目前頁面位於內嵌框架中',
      unsupported: '未偵測到螢幕分享介面', 'policy-blocked': '頁面權限原則禁止螢幕分享',
    },
  },
  ja: {
    title: 'ブラウザー情報', hint: '問題の報告時にこの情報をコピーすると、原因の特定に役立ちます。',
    copy: 'ブラウザー情報をコピー', copying: 'コピー中…', copied: 'コピー済み', retryCopy: '再度コピー',
    copyDone: 'ブラウザー情報をコピーしました。問題の報告に貼り付けられます。',
    copyFailed: 'コピーできませんでした。再試行するか、技術情報を開いて手動で選択・コピーしてください。',
    environment: '利用環境', features: '主な機能', details: '技術情報',
    browser: 'ブラウザー', operatingSystem: 'OS', language: 'ブラウザーの言語', timeZone: 'タイムゾーン',
    screen: '画面サイズ', viewport: '表示領域', pixelRatio: 'デバイスピクセル比', cssPixels: '{width} × {height} CSS px',
    unknown: '不明', unknownVersion: 'バージョン不明', chromiumFork: 'Chromium ベースのブラウザー',
    capture: '画面共有', documentPip: '最前面ウィジェット', wasmSimd: 'ローカル高速認識',
    supported: '対応', unavailable: '利用不可', restricted: '現在制限あり', apiAvailable: 'API あり', writable: '書き込み確認済み',
    featureHint: '検出結果は権限の付与を意味しません。画面共有には手動の許可が必要です。',
    technicalHint: '「API あり」はブラウザーが API を提供することを示します。権限、保存容量、ハードウェアアクセラレーションは未確認です。',
    userAgent: 'User-Agent', secureContext: '安全なコンテキスト', embedded: '埋め込みページ', yes: 'はい', no: 'いいえ',
    offscreenCanvas: 'OffscreenCanvas', canvas2d: 'Canvas 2D', createImageBitmap: 'ImageBitmap', moduleWorker: 'Worker',
    subtleCrypto: 'Web Crypto', cacheApi: 'Cache API', indexedDb: 'IndexedDB', localStorage: 'localStorage',
    captureReason: '画面共有の制限理由',
    captureIssues: {
      mobile: 'モバイルブラウザー', 'insecure-context': '安全なコンテキストが必要', embedded: 'ページが埋め込まれています',
      unsupported: '画面共有 API がありません', 'policy-blocked': 'ページの権限ポリシーで禁止されています',
    },
  },
  ko: {
    title: '브라우저 정보', hint: '문제를 신고할 때 이 정보를 복사하면 원인을 더 빠르게 파악할 수 있습니다.',
    copy: '브라우저 정보 복사', copying: '복사 중…', copied: '복사됨', retryCopy: '다시 복사',
    copyDone: '브라우저 정보를 복사했습니다. 문제 신고에 붙여넣을 수 있습니다.',
    copyFailed: '복사하지 못했습니다. 다시 시도하거나 기술 세부 정보를 펼쳐 직접 선택하고 복사해 주세요.',
    environment: '사용 환경', features: '주요 기능', details: '기술 세부 정보',
    browser: '브라우저', operatingSystem: '운영 체제', language: '브라우저 언어', timeZone: '시간대',
    screen: '화면 크기', viewport: '페이지 표시 영역', pixelRatio: '기기 픽셀 비율', cssPixels: '{width} × {height} CSS px',
    unknown: '알 수 없음', unknownVersion: '버전 확인 불가', chromiumFork: 'Chromium 기반 브라우저',
    capture: '화면 공유', documentPip: '항상 위에 표시되는 위젯', wasmSimd: '로컬 고속 인식',
    supported: '지원', unavailable: '사용 불가', restricted: '현재 제한됨', apiAvailable: 'API 제공됨', writable: '쓰기 확인됨',
    featureHint: '감지 결과는 권한 부여를 의미하지 않습니다. 화면 공유는 직접 허용해야 합니다.',
    technicalHint: '“API 제공됨”은 브라우저가 해당 API를 제공한다는 뜻입니다. 권한, 저장 용량, 하드웨어 가속 상태는 확인하지 않습니다.',
    userAgent: 'User-Agent', secureContext: '보안 컨텍스트', embedded: '내장 페이지', yes: '예', no: '아니요',
    offscreenCanvas: 'OffscreenCanvas', canvas2d: 'Canvas 2D', createImageBitmap: 'ImageBitmap', moduleWorker: 'Worker',
    subtleCrypto: 'Web Crypto', cacheApi: 'Cache API', indexedDb: 'IndexedDB', localStorage: 'localStorage',
    captureReason: '화면 공유 제한 사유',
    captureIssues: {
      mobile: '모바일 브라우저', 'insecure-context': '보안 컨텍스트 필요', embedded: '페이지가 프레임에 내장되어 있음',
      unsupported: '화면 공유 API 없음', 'policy-blocked': '페이지 권한 정책에 의해 차단됨',
    },
  },
}
